/**
 * Notes = texte brut uniquement.
 * Jamais interprété comme HTML, SQL ou code.
 */

import {
  asPlainString,
  getUtf8Bytes,
  normalizeSafeId,
  sanitizeMultiline,
  sanitizeSingleLine,
} from "./plainTextSanitize";

export const NOTE_TITLE_MIN_LENGTH = 3;
export const NOTE_TITLE_MAX_LENGTH = 60;
export const NOTE_CONTENT_MAX_BYTES = 100 * 1024;
export const DEFAULT_NOTE_CATEGORY = "autre";
export const DEFAULT_NOTE_TITLE = "Nouvelle note";
export { asPlainString };

export const NOTE_CATEGORY_VALUES = [
  "reperes_texte",
  "reperes_historiques",
  "faits_interessants",
  "essais_litteraires",
  "autre",
];
export const NOTE_CATEGORY_SET = new Set(NOTE_CATEGORY_VALUES);

export function getNoteContentBytes(content) {
  return getUtf8Bytes(typeof content === "string" ? content : "");
}

export function sanitizeNoteTitle(raw) {
  return sanitizeSingleLine(raw);
}

export function sanitizeNoteContent(raw) {
  return sanitizeMultiline(raw);
}

export function normalizeNoteCategory(raw) {
  const value = asPlainString(raw).trim();
  return NOTE_CATEGORY_SET.has(value) ? value : "";
}

export function normalizeNoteId(raw) {
  return normalizeSafeId(raw);
}

function sanitizeTimestamp(raw) {
  const value = asPlainString(raw).trim();
  if (!value) return null;
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return null;
  return new Date(time).toISOString();
}

export function validateNoteTitle(title) {
  const value = sanitizeNoteTitle(title);
  if (value.length < NOTE_TITLE_MIN_LENGTH) {
    return `Le titre doit contenir au moins ${NOTE_TITLE_MIN_LENGTH} caractères.`;
  }
  if (value.length > NOTE_TITLE_MAX_LENGTH) {
    return `Le titre ne peut pas dépasser ${NOTE_TITLE_MAX_LENGTH} caractères.`;
  }
  return null;
}

export function validateNoteContent(content) {
  const value = sanitizeNoteContent(content);
  if (!value.trim()) {
    return "Le contenu de la note ne peut pas être vide.";
  }
  if (getNoteContentBytes(value) > NOTE_CONTENT_MAX_BYTES) {
    return "Le contenu de la note est trop long.";
  }
  return null;
}

export function hasNoteContent(content) {
  return sanitizeNoteContent(content).trim().length > 0;
}

export function sanitizeNoteRecord(raw, { requireId = false } = {}) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const providedId = asPlainString(raw.noteId).trim();
  const noteId = normalizeNoteId(raw.noteId);
  if (requireId && !noteId) return null;
  if (providedId && !noteId) return null;

  const title = sanitizeNoteTitle(raw.title);
  const content = sanitizeNoteContent(raw.content);
  const category = normalizeNoteCategory(raw.category) || DEFAULT_NOTE_CATEGORY;

  return {
    noteId: noteId || null,
    title,
    content,
    category,
    contentBytes: getNoteContentBytes(content),
    createdAt: sanitizeTimestamp(raw.createdAt),
    updatedAt: sanitizeTimestamp(raw.updatedAt),
  };
}
